import { useEffect, useRef, useState } from 'react'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { Spinner } from '@forwardflow/ui/Spinner'
import LocalStorage from '@forwardflow/services/LocalStorage'
import AuthService from '@forwardflow/services/AuthService'
import SocialLogin from './SocialLogin'
import { SignupForm } from '@forwardflow/forms'
import Icon from '@forwardflow/ui/Icons'
import { useUserNavigation } from '@forwardflow/hooks/customHooks/useUserNavigation'
import { assistantAuthPath, clearHandle, hasPendingHandle, isValidHandle, signupVerificationUrl } from '@forwardflow/services/AssistantConnect'

const SignupComponent = () => {
   const [searchParams] = useSearchParams()
   const { assistantHandle } = useParams()
   const continuationParams = new URLSearchParams(searchParams)
   if (assistantHandle !== undefined) continuationParams.set('handle', assistantHandle)
   const attemptedToken = useRef<string | null>(null)
   const [verificationError, setVerificationError] = useState('')
   const [socialLogin, setSocialLogin] = useState(false)
   const [isSubmitted, setIsSubmitted] = useState(false)
   const [emailFromUrl, setEmailFromUrl] = useState<string | null>(null)
   const [nameFromUrl, setNameFromUrl] = useState<string | null>(null)
   const [email, setEmail] = useState<string | undefined>(undefined)
   const [showSignupForm, setShowSignupForm] = useState(false)
   const [showForm, setShowForm] = useState(false)
   const [verificationResult, setVerificationResult] = useState<{
      verified: boolean
   }>({
      verified: false,
   })
   const [resendSent, setResendSent] = useState(false)
   const [resendError, setResendError] = useState('')
   const [isResending, setIsResending] = useState(false)
   const [cooldown, setCooldown] = useState(0)
   const { navigateUser, getCurrentOrgId } = useUserNavigation()
   const demoBookingId = searchParams.get('demoBookingId')

   const handleSocialLogin = async () => {
      let data: { accessToken: string } | null = null
      const authPlatform = searchParams.get('auth')
      // get temporary token from search params
      const tempToken = searchParams.get('token')
      if (tempToken === null) return
      if (attemptedToken.current === tempToken) return
      attemptedToken.current = tempToken
      if (continuationParams.has('handle') && !isValidHandle(continuationParams.get('handle'))) {
         clearHandle()
         setVerificationError('This assistant connection link is invalid. Restart the connection from ChatGPT or Claude.')
         return
      }
      try {
         setSocialLogin(true)
         if (
            authPlatform === 'google' ||
            authPlatform === 'outlook' ||
            authPlatform === 'microsoft'
         ) {
            data = await AuthService.apiGenerateAccessToken(tempToken)
         } else {
            data = await AuthService.apiValidateEmail(tempToken)
         }
         if (data && data.accessToken) {
            LocalStorage.put('access_token', data.accessToken)
            if (hasPendingHandle(continuationParams)) {
               await navigateUser({ searchParams: continuationParams.toString() })
               return
            }
            const currentOrgId = await getCurrentOrgId()
            navigateUser({
               currentOrgId: currentOrgId || '',
               searchParams: continuationParams.toString(),
            })
         } else {
            setVerificationError('Email verification could not be completed. Request a new verification email and try again.')
            setSocialLogin(false)
         }
      } catch (error) {
         console.error('error', error)
         setVerificationError('Email verification could not be completed. Request a new verification email and try again.')
         setSocialLogin(false)
      }
   }
   const handleSubmit = (submitted: boolean, email?: string) => {
      if (submitted) {
         setIsSubmitted(true)
         setEmail(email)
      } else {
         setIsSubmitted(false)
      }
   }

   const handleShowSignupForm = () => {
      setShowSignupForm(true)
   }

   useEffect(() => {
      if (cooldown <= 0) return
      const timer = setInterval(() => {
         setCooldown((prev) => prev - 1)
      }, 1000)
      return () => clearInterval(timer)
   }, [cooldown])

   useEffect(() => {
      const email = searchParams.get('email')
      const name = searchParams.get('name')
      if (email) {
         setEmailFromUrl(email)
      }
      if (name) {
         setNameFromUrl(name)
      }

      async function isGoogleMailVerified(domain: string) {
         const apiUrl = `https://dns.google.com/resolve?name=${domain}&type=MX`

         try {
            const response = await fetch(apiUrl)
            const data = await response.json()

            if (!data.Answer) {
               return { verified: false }
            }

            let hasVerificationRecord = false

            for (const record of data.Answer) {
               if (record.data.includes('google')) {
                  hasVerificationRecord = true
               }
            }

            if (hasVerificationRecord) {
               return {
                  verified: true,
               }
            }
         } catch (error) {
            return { verified: false }
         }
      }

      if (emailFromUrl) {
         isGoogleMailVerified(emailFromUrl.split('@')[1]).then((result) => {
            console.log(result)
            setVerificationResult(result as any)
         })
      }

      handleSocialLogin()
   }, [searchParams, emailFromUrl])

   return (
      <>
         <div
            className={`flex min-h-screen px-4 lg:px-[30px] flex-col  items-center text-center pt-14 md:pt-28`}
         >
            <a href="/">
               <img
                  width={'165px'}
                  src="/images/fff-logo.png"
                  className="md:absolute left-10 top-10"
                  alt="ff icon"
               />
            </a>
            <Link
               className="bg-white/10 transition-all hover:bg-white/20 w-[200px] h-12 flexc rounded-full md:absolute mt-7 md:mt-0 top-10 right-4 md:right-16"
               to={assistantAuthPath('/signin', continuationParams)}
            >
               Sign in
            </Link>
            <h1 className="text-[20px] md:text-[24px] mt-[30px] mb-7 font-medium">
               Create Your Forward Flow Account
            </h1>
            <h2 className="text-[12px] md:text-[14px] mb-[54px] text-[#C9C9C9] max-w-[450px] mx-auto">
               Experience instant setup. Connect your Google account to unlock
               calendar syncing automatically.
            </h2>

            {verificationError && <p role="alert" className="text-red-400 text-sm mb-4">{verificationError}</p>}
            {!socialLogin ? (
               !isSubmitted ? (
                  <>
                     <div className="w-full flex flex-col max-w-[550px] items-start mb-10">
                        {(!verificationResult?.verified || showSignupForm) && (
                           <>
                              <SignupForm
                                 continuationSearch={continuationParams.toString()}
                                 setShowForm={setShowForm}
                                 showForm={showForm}
                                 onSubmit={handleSubmit}
                                 initialEmail={emailFromUrl as string}
                                 firstName={nameFromUrl?.split(' ')[0] || ''}
                                 lastName={
                                    nameFromUrl
                                       ?.split(' ')
                                       .slice(1)
                                       .join(' ') || ''
                                 }
                              />
                              {showForm && (
                                 <div className="flex items-center w-full gap-x-3">
                                    <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                                    <span className="text-[12px]">OR</span>
                                    <span className="h-[2px] bg-white/30 w-full flex flex-1"></span>
                                 </div>
                              )}
                           </>
                        )}
                     </div>
                     {showForm && (
                        <SocialLogin
                           demoBookingId={
                              demoBookingId
                                 ? parseInt(demoBookingId)
                                 : undefined
                           }
                        />
                     )}
                     {!(!verificationResult?.verified || showSignupForm) && (
                        <button
                           className="mt-2 rounded-xl group text-[14px] md:text-[18px] bg-black/40 h-14 md:h-[70px] max-w-[550px] w-full flexc relative border border-white/10 hover:border-white hover:bg-primary transition"
                           onClick={handleShowSignupForm}
                        >
                           Sign up to Forward Flow
                        </button>
                     )}
                  </>
               ) : (
                  <>
                     <div className="w-full max-w-[550px] mx-auto bg-black/40 border border-white/20 rounded-xl p-8 flex flex-col justify-center">
                        <Icon name="Mail" className="mx-auto mb-5" />
                        <h2 className="text-[20px] font-semibold mb-10">
                           Check your email
                        </h2>
                        <div className="text-[14px] leading-8">
                           <p className="text-white/70">
                              We emailed a magic link to
                           </p>
                           <p>{email}</p>
                           <p className="mb-4 text-white/70">
                              Click the link to finish sign up
                           </p>
                        </div>
                        <p className="mt-2 text-[13px] text-white/50">
                           Didn't receive it?{' '}
                           <button
                              onClick={async () => {
                                 try {
                                    setIsResending(true)
                                    setResendSent(false)
                                    setResendError('')
                                    const redirectUrl = signupVerificationUrl(window.location.origin, continuationParams)
                                    const res = await AuthService.apiResendVerificationMail(redirectUrl)
                                    if (res?.success === false && res?.timeRemaining) {
                                       setCooldown(res.timeRemaining)
                                       setResendError(res.message || 'Please wait before resending')
                                    } else {
                                       setResendSent(true)
                                       setCooldown(60)
                                    }
                                 } catch (error) {
                                    console.error('Error resending verification email:', error)
                                    setResendError('Failed to resend email. Please try again.')
                                 } finally {
                                    setIsResending(false)
                                 }
                              }}
                              disabled={isResending || cooldown > 0}
                              className="text-[#9061DA] hover:text-[#a87ee6] transition-colors disabled:text-white/30 disabled:cursor-not-allowed"
                           >
                              {isResending
                                 ? 'Sending...'
                                 : cooldown > 0
                                    ? `Resend in ${cooldown}s`
                                    : 'Resend'}
                           </button>
                        </p>
                        {resendSent && (
                           <p className="mt-2 text-green-500 text-[13px]">
                              Verification email sent!
                           </p>
                        )}
                        {resendError && (
                           <p className="mt-2 text-red-400 text-[13px]">
                              {resendError}
                           </p>
                        )}
                     </div>
                  </>
               )
            ) : (
               <Spinner />
            )}
            <div className="my-7 flex justify-between max-w-[550px] w-full">
               <a
                  href="/terms"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Terms and Conditions
               </a>
               <a
                  href="/privacy"
                  className="text-[#CDCDCD] text-[12px] font-medium hover:text-[#CDCDCD]/80"
               >
                  Privacy Policy
               </a>
            </div>
         </div>
      </>
   )
}

export default SignupComponent
